/**
 * کلیدهای query و پیکربندی TanStack Query.
 *
 * کلیدها متمرکزند تا بی‌اعتبارسازی قابل اتکا باشد: با کلید دست‌ساز در هر
 * کامپوننت، یک تغییر در ساختار کلید باعث می‌شد `invalidateQueries` بی‌صدا هیچ
 * چیزی را تازه نکند و کاربر داده‌ی قدیمی ببیند.
 */

import { QueryClient } from '@tanstack/react-query'

import { ApiError } from './api/client'

import type { Scope } from './api/endpoints'

/** کلید scope به شکل tuple، تا بی‌اعتبارسازی سلسله‌مراتبی کار کند. */
const scopeKey = (scope: Scope): [string, string] => [
  scope.organizationId,
  scope.assistantId,
]

export const queryKeys = {
  meta: ['meta'] as const,
  consultation: (id: string) => ['consultation', id] as const,
  caseRows: (id: string) => ['consultation', id, 'case'] as const,
  /** پیشوند مشترک آرشیو. برای بی‌اعتبارسازی همه‌ی نماهای آرشیو با یک فراخوانی. */
  archivePrefix: ['archive'] as const,
  archive: (query: string, scope?: Scope) =>
    ['archive', query, scope === undefined ? '' : scopeKey(scope)] as const,
  archiveEntry: (id: string) => ['archive', 'entry', id] as const,
  organizations: ['organizations'] as const,
  organizationDocuments: (org: string) => ['organizations', org, 'documents'] as const,
  people: (org: string) => ['organizations', org, 'people'] as const,
  processing: (scope: Scope) => ['processing', ...scopeKey(scope)] as const,
  assistants: (org: string) => ['organizations', org, 'assistants'] as const,
  assistantProfile: (scope: Scope) => ['assistant', ...scopeKey(scope), 'profile'] as const,
  dossiers: (scope: Scope, query: string, status?: string) =>
    ['dossiers', ...scopeKey(scope), query, status ?? ''] as const,
  dossier: (scope: Scope, id: string) => ['dossier', ...scopeKey(scope), id] as const,
  /** پیشوند مشترک مرور اصول، برای بی‌اعتبارسازی همه‌ی فیلترها با یک فراخوانی. */
  principlesPrefix: ['principles'] as const,
  principles: (scope: Scope, filters: string) =>
    ['principles', ...scopeKey(scope), filters] as const,
  dossierDocuments: (scope: Scope, id: string) =>
    ['dossier', ...scopeKey(scope), id, 'documents'] as const,
  publicationPlan: (scope: Scope) => ['publication', ...scopeKey(scope), 'plan'] as const,
  activePublication: (scope: Scope) =>
    ['publication', ...scopeKey(scope), 'active'] as const,
  revisions: (scope: Scope) => ['revisions', ...scopeKey(scope)] as const,
  /** وضعیت نسخه‌ی زنده‌ی یک دستیار. */
  live: (scope: Scope) => ['live', ...scopeKey(scope)] as const,
  /** همه‌ی نسخه‌های زنده‌ی این نمونه. */
  liveList: ['live', 'all'] as const,
  job: (id: string) => ['job', id] as const,
  profile: ['settings', 'profile'] as const,
  retrieval: ['settings', 'retrieval'] as const,
  secrets: ['settings', 'secrets'] as const,
} as const

/**
 * تلاش مجدد فقط برای خطاهای گذرا.
 *
 * ۴۰۱، ۴۰۳، ۴۰۴ و ۴۰۹ با تلاش دوباره درست نمی‌شوند و تکرارشان فقط سرور را
 * مشغول می‌کند. مهم‌تر: تلاش مجدد روی مسیر مشاوره یعنی تماس LLM دوباره و خرج
 * دوباره، پس نباید کور انجام شود.
 */
const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (error instanceof ApiError) {
    if (error.status >= 400 && error.status < 500) {
      return false
    }
  }
  return failureCount < 2
}

export const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // ۳۰ ثانیه: داده‌ی این رابط تند عوض نمی‌شود و درخواست دوباره در هر
        // focus پنجره، بی‌فایده است.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // جهش هرگز خودکار تکرار نمی‌شود. یک `POST /publication` تکراری یعنی
        // تماس پولی دوباره.
        retry: false,
      },
    },
  })
